// Shared curriculum + subject constants for ArdNotes

// Hero artwork — pelicans + academic doodles
export const HERO_ARTWORK_URL =
  'https://customer-assets.emergentagent.com/job_learn-share-30/artifacts/93kajb6p_C9636ADF-62C4-4A54-8CC0-B7903963CA75.png';

export const CURRICULA = [
  {
    name: 'IGCSE',
    description: 'International General Certificate of Secondary Education — Remove & Fifth Form',
    accent: 'from-indigo-400 to-indigo-500',
    light: 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800',
    badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300',
    levels: null,
  },
  {
    name: 'A-Level',
    description: 'Advanced Level — Sixth Form (AS & A2)',
    accent: 'from-violet-400 to-violet-500',
    light: 'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800',
    badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300',
    levels: ['AS', 'A2'],
  },
  {
    name: 'IB',
    description: 'International Baccalaureate — Sixth Form Diploma Programme (HL & SL)',
    accent: 'from-purple-400 to-purple-500',
    light: 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800',
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
    levels: ['HL', 'SL'],
  },
];

// Base shared subjects (IGCSE & A-Level use this directly)
const BASE_SUBJECTS = [
  { name: 'Mathematics', icon: '📐', description: 'Pure, mechanics, and statistics', color: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800' },
  { name: 'Further Maths', icon: '🔢', description: 'Advanced mathematics for further studies', color: 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800' },
  { name: 'Physics', icon: '⚛️', description: 'Mechanics, waves, electricity, and modern physics', color: 'bg-cyan-50 dark:bg-cyan-950/30 border-cyan-200 dark:border-cyan-800' },
  { name: 'Chemistry', icon: '🧪', description: 'Organic, inorganic, and physical chemistry', color: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' },
  { name: 'Biology', icon: '🧬', description: 'Cell biology, genetics, and ecology', color: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' },
  { name: 'Economics', icon: '📊', description: 'Micro and macroeconomics principles', color: 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800' },
  { name: 'Computer Science', icon: '💻', description: 'Programming, algorithms, and data structures', color: 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800' },
  { name: 'English Literature', icon: '📖', description: 'Poetry, prose, drama, and literary analysis', color: 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800' },
  { name: 'English Language', icon: '✍️', description: 'Grammar, rhetoric, and language analysis', color: 'bg-pink-50 dark:bg-pink-950/30 border-pink-200 dark:border-pink-800' },
  { name: 'History', icon: '🏛️', description: 'Modern and world history topics', color: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800' },
  { name: 'Geography', icon: '🌍', description: 'Physical, human, and environmental geography', color: 'bg-teal-50 dark:bg-teal-950/30 border-teal-200 dark:border-teal-800' },
  { name: 'Psychology', icon: '🧠', description: 'Cognitive, social, and developmental psychology', color: 'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800' },
  { name: 'Sociology', icon: '👥', description: 'Society, culture, and social institutions', color: 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-800' },
  { name: 'Business Studies', icon: '💼', description: 'Marketing, finance, and business operations', color: 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800' },
  { name: 'Spanish', icon: '🇪🇸', description: 'Spanish language, grammar, and culture', color: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800' },
  { name: 'French', icon: '🇫🇷', description: 'French language, grammar, and literature', color: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800' },
  { name: 'German', icon: '🇩🇪', description: 'German language, grammar, and culture', color: 'bg-stone-50 dark:bg-stone-950/30 border-stone-200 dark:border-stone-800' },
  { name: 'Chinese', icon: '🇨🇳', description: 'Mandarin Chinese, characters, and culture', color: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800' },
  { name: 'Russian', icon: '🇷🇺', description: 'Russian language, grammar, and literature', color: 'bg-slate-50 dark:bg-slate-950/30 border-slate-200 dark:border-slate-800' },
  { name: 'Latin', icon: '📜', description: 'Classical Latin language and Roman texts', color: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800' },
];

// IB has its own maths split + no Russian
const IB_MATHS = [
  { name: 'Maths AA', icon: '∫', description: 'Analysis & Approaches — calculus-heavy pure maths', color: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800' },
  { name: 'Maths AI', icon: '📈', description: 'Applications & Interpretation — applied & statistics', color: 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800' },
];

const IB_SUBJECTS = [
  ...IB_MATHS,
  ...BASE_SUBJECTS.filter((s) => !['Mathematics', 'Further Maths', 'Russian'].includes(s.name)),
];

export const SUBJECTS = BASE_SUBJECTS;
export const SUBJECT_NAMES = BASE_SUBJECTS.map((s) => s.name);

// Curriculum-specific subject lists
const SUBJECTS_BY_CURRICULUM = {
  IGCSE: BASE_SUBJECTS,
  'A-Level': BASE_SUBJECTS,
  IB: IB_SUBJECTS,
};

export const getSubjectsForCurriculum = (curriculumName) =>
  SUBJECTS_BY_CURRICULUM[curriculumName] || BASE_SUBJECTS;

export const getSubjectNamesForCurriculum = (curriculumName) =>
  getSubjectsForCurriculum(curriculumName).map((s) => s.name);

export const CURRICULUM_NAMES = CURRICULA.map((c) => c.name);
export const getCurriculum = (name) => CURRICULA.find((c) => c.name === name);
